import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { initials } from '../lib/format';
import { Modal } from './ui';
import { Icon } from './Icon';

/**
 * الأشخاص في الواجهة.
 *
 * `ownerId` في العميل والعميل المحتمل نصٌّ مجرّد لا علاقة في Prisma: جدول
 * المستخدمين في القاعدة العامة بينما العميل داخل نطاق المستأجر وسياسات
 * RLS، فلا ضمّ بينهما. البديلان كانا:
 *
 *   أ. أن يحلّ الخادم المعرّف في كل مسار يُعيد سجلًا له مسؤول — وهو جلبٌ
 *      إضافي في كل قائمة وكل تفصيل، يتكرر في عشرين موضعًا وينسى أحدها.
 *   ب. أن تجلب الواجهة دليل الفريق مرة وتحلّ به كل معرّف.
 *
 * اخترنا الثاني: الفريق عشرات لا آلاف، والقائمة تُجلب مرة وتُخزَّن، ومسار
 * `/team/members` متاح لكل عضو أصلًا لأن معرفة الزملاء جزء من العمل.
 */

export interface TeamMember {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: 'OWNER' | 'ADMIN' | 'MANAGER' | 'SALES' | 'EMPLOYEE';
  status: string;
  avatarUrl: string | null;
  jobTitle: string | null;
}

/**
 * دليل الفريق، مجلوبًا مرة ومُحلًّى بخريطة.
 *
 * `staleTime` طويل عمدًا: أسماء الزملاء وصورهم لا تتغير أثناء الجلسة،
 * وإعادة جلبها مع كل شاشة تُنتج طلبًا لكل تنقّل بلا فائدة.
 */
export function useTeamDirectory() {
  const q = useQuery({
    queryKey: ['team', 'directory'],
    queryFn: () => api<TeamMember[]>('/team/members'),
    staleTime: 5 * 60_000,
  });

  const byId = useMemo(() => {
    const map = new Map<string, TeamMember>();
    for (const m of q.data ?? []) map.set(m.userId, m);
    return map;
  }, [q.data]);

  /* المسنَد إليهم العمل: الموقوف لا يُسنَد إليه جديد لكنه يظل معروضًا فيما مضى. */
  const assignable = useMemo(
    () => (q.data ?? []).filter((m) => m.status === 'ACTIVE'),
    [q.data],
  );

  return { members: q.data ?? [], assignable, byId, isPending: q.isPending };
}

/**
 * درجة لون ثابتة مشتقّة من الاسم.
 *
 * الثبات هو المقصود: الشخص نفسه يأخذ اللون نفسه في كل شاشة وكل جلسة، فيصير
 * اللون علامة تُعرَف قبل قراءة الاسم. لونٌ عشوائي في كل رسم يُفقد ذلك.
 */
function hueOf(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

export function Avatar({
  name,
  src,
  size = 30,
  title,
}: {
  name: string;
  src?: string | null;
  size?: number;
  title?: string;
}) {
  const style = {
    width: size,
    height: size,
    fontSize: Math.max(10, Math.round(size * 0.38)),
    ['--av-h' as string]: hueOf(name || '?'),
  };

  if (src) {
    return (
      <img
        className="avatar avatar-img"
        src={src}
        alt=""
        style={style}
        title={title ?? name}
        loading="lazy"
      />
    );
  }

  return (
    <span className="avatar avatar-tinted" style={style} title={title ?? name} aria-hidden="true">
      {initials(name || '؟')}
    </span>
  );
}

/**
 * المسؤول معروضًا: صورة واسم.
 *
 * غير المسنَد يُعرض صراحةً لا فارغًا — الخلية الفارغة تُقرأ خطأً في العرض،
 * بينما «بلا مسؤول» حالةٌ يتصرّف فيها المدير.
 */
export function OwnerChip({
  ownerId,
  size = 24,
  showName = true,
  empty = 'بلا مسؤول',
}: {
  ownerId: string | null | undefined;
  size?: number;
  showName?: boolean;
  empty?: string;
}) {
  const { byId } = useTeamDirectory();

  if (!ownerId) {
    return <span className="owner-chip owner-chip-empty">{empty}</span>;
  }

  const member = byId.get(ownerId);
  /*
   * المعرّف بلا عضو: من غادر الشركة وحُذفت عضويته وبقيت سجلاته منسوبة إليه.
   * عرضه «مستخدم سابق» أصدق من عرضه فارغًا أو عرض المعرّف الخام.
   */
  const name = member?.name ?? 'مستخدم سابق';

  return (
    <span className="owner-chip" title={member ? `${name} — ${member.email}` : name}>
      <Avatar name={name} src={member?.avatarUrl} size={size} />
      {showName && <span className="owner-chip-name">{name}</span>}
    </span>
  );
}

/**
 * اختيار المسؤول.
 *
 * قائمة بالصور لا `select` نصّي: من ينقل عميلًا يبحث عن وجه يعرفه في فريق
 * من عشرين، والقراءة بالصورة أسرع من قراءة عشرين سطرًا متشابهًا. والبحث
 * موجود لأن الفريق قد يتجاوز ما تسعه الشاشة.
 */
export function OwnerPickerModal({
  currentId,
  title,
  pending,
  error,
  onPick,
  onClose,
}: {
  currentId: string | null;
  title: string;
  pending: boolean;
  error: unknown;
  onPick: (userId: string | null) => void;
  onClose: () => void;
}) {
  const { assignable, isPending } = useTeamDirectory();
  const [q, setQ] = useState('');

  const needle = q.trim().toLowerCase();
  const shown = needle
    ? assignable.filter(
        (m) => m.name.toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle),
      )
    : assignable;

  return (
    <Modal title={title} onClose={onClose}>
      {error != null && (
        <div className="alert alert-danger mb-4">
          {error instanceof Error ? error.message : 'تعذّر تغيير المسؤول.'}
        </div>
      )}

      <div className="field mb-4">
        <input
          className="input"
          placeholder="بحث بالاسم أو البريد"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="بحث في الفريق"
          autoComplete="off"
        />
      </div>

      {isPending && <div className="skeleton" style={{ height: 120 }} />}

      <div className="pick-list">
        {shown.map((m) => (
          <button
            key={m.userId}
            type="button"
            className={`pick-row${m.userId === currentId ? ' pick-row-current' : ''}`}
            disabled={pending || m.userId === currentId}
            onClick={() => onPick(m.userId)}
          >
            <Avatar name={m.name} src={m.avatarUrl} size={30} />
            <span className="pick-row-text">
              <strong>{m.name}</strong>
              <small>{m.jobTitle ?? m.email}</small>
            </span>
            {m.userId === currentId && <Icon name="check" size={16} />}
          </button>
        ))}

        {!isPending && shown.length === 0 && (
          <div className="alert alert-info">لا أحد يطابق البحث.</div>
        )}
      </div>

      <button
        type="button"
        className="btn mt-4"
        disabled={pending || !currentId}
        onClick={() => onPick(null)}
      >
        <Icon name="x" size={15} />
        إزالة الإسناد
      </button>
    </Modal>
  );
}
